import Konva from 'konva';
import React from 'react';
import { Group, Rect, Text } from 'react-konva';
import { useFeatureFlags } from '../../services/featureFlags/hooks/useFeatureFlags';
import { useTranslation } from '../../services/translation/hooks/useTranslation';
import { DiagnosticFeature } from '../commonUI/viewMode/DiagnosticFeature';
import { isFeatureEnum } from '../commonUtils';
import { roundFractional } from '../ranches/ranchUtils';
import { MaskTooltipInfo } from './ImageStage';

interface Props {
  widthParent: number;
  heightParent: number;
  maskTooltipInfo: MaskTooltipInfo;
}

const MaskTooltip: React.FC<Props> = ({
  widthParent,
  heightParent,
  maskTooltipInfo,
}) => {
  const { Diagnostics } = useFeatureFlags();
  const {
    visible,
    mousePosX,
    mousePosY,
    feature,
    score,
    distance,
    model,
    className,
    modelVersion,
  } = maskTooltipInfo;
  const { t } = useTranslation();

  if (!visible) {
    return null;
  }

  const textPadding = 10;
  const lineHeight = 20;

  const isValidFeature = isFeatureEnum(feature);

  const featureText = `Feature: ${
    feature ? (isValidFeature ? t(feature, { ns: 'common' }) : feature) : 'N/A'
  }`;
  const scoreText = `Confidence: ${
    score ? `${roundFractional(score * 100, 1)}%` : 'N/A'
  }`;
  const distanceText = `Distance: ${
    distance ? roundFractional(distance, 1) : 'N/A'
  }`;
  const modelText = `Service: ${model ? model : 'N/A'}`;
  const classText = `Class: ${className ? className : 'N/A'}`;
  const modelVersionText = `Version: ${modelVersion ? modelVersion : 'N/A'}`;

  const maxWidth = Math.max(
    getTextWidth(featureText),
    getTextWidth(scoreText),
    Diagnostics
      ? Math.max(
          getTextWidth(distanceText),
          getTextWidth(modelText),
          getTextWidth(classText),
          getTextWidth(modelVersionText)
        )
      : 0
  );

  const tooltipWidth = maxWidth + 2 * textPadding;
  const tooltipHeight = (Diagnostics ? 6 : 2) * lineHeight + 1.3 * textPadding;
  // const tooltipPosY = mousePosY - tooltipHeight - 10;
  // center tooltip on mouse position.  if tooltip would go off screen, adjust top or bottom
  const tooltipPosY = (() => {
    const topEdge = 0;
    const bottomEdge = heightParent;

    const centeredPosY = mousePosY - tooltipHeight - 10;
    const topAdjustedPosY = mousePosY + 10; // moved tooltip downward by 10px
    const bottomAdjustedPosY = mousePosY - tooltipHeight - 10;

    if (centeredPosY >= topEdge && centeredPosY + tooltipHeight <= bottomEdge) {
      if (
        centeredPosY <= mousePosY &&
        mousePosY <= centeredPosY + tooltipHeight
      ) {
        // If the tooltip would appear under the cursor, move it downwards if there is enough space
        if (topAdjustedPosY + tooltipHeight <= bottomEdge) {
          return topAdjustedPosY;
        }
      }
      return centeredPosY;
    } else if (centeredPosY < topEdge) {
      return topAdjustedPosY;
    } else {
      return bottomAdjustedPosY;
    }
  })();

  // center tooltip on mouse position.  If tooltip would go off screen, adjust left or right
  const tooltipPosX = (() => {
    const leftEdge = 0;
    const rightEdge = widthParent;

    const centeredPosX = mousePosX - tooltipWidth / 2;
    const leftAdjustedPosX = mousePosX;
    const rightAdjustedPosX = mousePosX - tooltipWidth;
    const rightAdjustedPosXAwayFromCursor = mousePosX + 30; // move tooltip to right by 30px
    const leftAdjustedPosXAwayFromCursor = mousePosX - tooltipWidth - 30; // move tooltip to left by 30px

    if (centeredPosX >= leftEdge && centeredPosX + tooltipWidth <= rightEdge) {
      if (
        centeredPosX <= mousePosX &&
        mousePosX <= centeredPosX + tooltipWidth
      ) {
        // If the tooltip would appear under the cursor, move it to the right or left based on available space
        if (rightAdjustedPosXAwayFromCursor + tooltipWidth <= rightEdge) {
          return rightAdjustedPosXAwayFromCursor;
        } else if (leftAdjustedPosXAwayFromCursor >= leftEdge) {
          return leftAdjustedPosXAwayFromCursor;
        }
      }
      return centeredPosX;
    } else if (centeredPosX < leftEdge) {
      return leftAdjustedPosX;
    } else {
      return rightAdjustedPosX;
    }
  })();

  function getTextWidth(text: string) {
    const textNode = new Konva.Text({ text, fontSize: 14 });
    return textNode.getTextWidth();
  }

  return (
    <Group>
      <Rect
        x={tooltipPosX}
        y={tooltipPosY}
        width={tooltipWidth}
        height={tooltipHeight}
        fill='black'
        cornerRadius={5}
        opacity={0.75}
      />
      <Text
        x={tooltipPosX + textPadding}
        y={tooltipPosY + textPadding}
        width={maxWidth}
        text={featureText}
        fontSize={14}
        fill='white'
        align='center'
      />
      <Text
        x={tooltipPosX + textPadding}
        y={tooltipPosY + textPadding + lineHeight}
        width={maxWidth}
        text={scoreText}
        fontSize={14}
        fill='white'
        align='center'
      />
      <DiagnosticFeature>
        <Text
          x={tooltipPosX + textPadding}
          y={tooltipPosY + textPadding + 2 * lineHeight}
          width={maxWidth}
          text={distanceText}
          fontSize={14}
          fill='white'
          align='center'
        />
        <Text
          x={tooltipPosX + textPadding}
          y={tooltipPosY + textPadding + 3 * lineHeight}
          width={maxWidth}
          text={modelText}
          fontSize={14}
          fill='white'
          align='center'
        />
        <Text
          x={tooltipPosX + textPadding}
          y={tooltipPosY + textPadding + 4 * lineHeight}
          width={maxWidth}
          text={classText}
          fontSize={14}
          fill='white'
          align='center'
        />
        <Text
          x={tooltipPosX + textPadding}
          y={tooltipPosY + textPadding + 5 * lineHeight}
          width={maxWidth}
          text={modelVersionText}
          fontSize={14}
          fill='white'
          align='center'
        />
      </DiagnosticFeature>
    </Group>
  );
};

export default React.memo(MaskTooltip);
